import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Receipt, ChevronLeft } from 'lucide-react';
import { phoneSchema, requestOtpSchema, verifyOtpSchema } from '@bstm/shared';
import type { UserDto } from '@bstm/shared';
import { z } from 'zod';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { toast } from 'sonner';

type Step = 'phone' | 'code';

const phoneFormSchema = z.object({ phone: phoneSchema });
const codeFormSchema = verifyOtpSchema;

function formatUsPhone(digits: string): string {
  const d = digits.replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '').slice(0, 10);
}

export default function Login() {
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState(''); // E.164 once normalized
  const [phoneInput, setPhoneInput] = useState(''); // formatted (XXX) XXX-XXXX
  const [submitting, setSubmitting] = useState(false);
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/';
  const nextQs = next && next !== '/' ? `?next=${encodeURIComponent(next)}` : '';

  const codeForm = useForm<z.infer<typeof codeFormSchema>>({
    resolver: zodResolver(codeFormSchema),
    defaultValues: { phone: '', code: '' },
    mode: 'onSubmit',
  });

  function onCodeInvalid(errors: typeof codeForm.formState.errors) {
    const msg = errors.code?.message ?? errors.phone?.message ?? 'Enter the 6-digit code';
    toast.error(msg);
  }

  async function onRequestOtp(e: React.FormEvent) {
    e.preventDefault();
    const digits = digitsOnly(phoneInput);
    if (digits.length !== 10) {
      toast.error('Enter a 10-digit US phone number');
      return;
    }
    const e164 = `+1${digits}`;
    const parsed = phoneFormSchema.safeParse({ phone: e164 });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? 'Invalid phone');
      return;
    }
    setSubmitting(true);
    try {
      await api('/auth/otp/request', { method: 'POST', json: requestOtpSchema.parse({ phone: e164 }) });
      setPhone(e164);
      codeForm.reset({ phone: e164, code: '' });
      setStep('code');
      toast.success('Code sent — check your phone');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to send code');
    } finally {
      setSubmitting(false);
    }
  }

  async function onVerifyOtp(values: z.infer<typeof codeFormSchema>) {
    try {
      const data = await api<{ token: string; user: UserDto }>('/auth/otp/verify', {
        method: 'POST',
        json: values,
      });
      loginWithToken(data.token);
      if (!data.user.name) {
        navigate(`/set-name${nextQs}`);
      } else if (data.user.status !== 'ACTIVE') {
        navigate(`/pending${nextQs}`);
      } else {
        navigate(next);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Invalid code');
    }
  }

  return (
    <div className="min-h-full flex items-center justify-center p-6 bg-gradient-to-b from-zinc-950 to-zinc-900">
      <div className="card p-8 w-full max-w-sm shadow-xl shadow-black/40">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-emerald-600/20 flex items-center justify-center">
            <Receipt size={18} className="text-emerald-400" />
          </div>
          <div>
            <h1 className="text-base font-semibold leading-tight">Ticket Manager</h1>
            <p className="text-xs text-zinc-500">
              {step === 'phone' ? 'Sign in via SMS' : 'Verify your code'}
            </p>
          </div>
        </div>

        {step === 'phone' && (
          <form onSubmit={onRequestOtp} className="space-y-4">
            <div>
              <label className="label" htmlFor="phone-input">Phone number</label>
              <div className="flex items-stretch rounded-md border border-zinc-800 bg-zinc-900 focus-within:border-zinc-600 transition-colors overflow-hidden">
                <span className="flex items-center px-3 text-zinc-400 border-r border-zinc-800 font-mono text-sm bg-zinc-950">
                  +1
                </span>
                <input
                  id="phone-input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  autoFocus
                  placeholder="(555) 123-4567"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(formatUsPhone(e.target.value))}
                  className="flex-1 border-0 bg-transparent focus:bg-transparent rounded-none px-3 py-2 outline-none font-mono"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1.5">US numbers only. We'll text you a 6-digit code.</p>
            </div>
            <button
              type="submit"
              className="btn-primary w-full justify-center text-sm"
              disabled={submitting || digitsOnly(phoneInput).length !== 10}
            >
              {submitting ? 'Sending…' : 'Send code'}
            </button>
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={codeForm.handleSubmit(onVerifyOtp, onCodeInvalid)} className="space-y-4">
            <button
              type="button"
              onClick={() => setStep('phone')}
              className="inline-flex items-center text-xs text-zinc-400 hover:text-white -ml-1"
            >
              <ChevronLeft size={14} /> Use a different number
            </button>
            <p className="text-sm text-zinc-300">
              Code sent to <span className="font-mono">{phone}</span>
            </p>
            <div>
              <label className="label" htmlFor="code">6-digit code</label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                maxLength={6}
                className="w-full font-mono tracking-[0.5em] text-center text-2xl py-3"
                {...codeForm.register('code')}
              />
              {codeForm.formState.errors.code && (
                <p className="field-error">{codeForm.formState.errors.code.message}</p>
              )}
            </div>
            <button
              type="submit"
              className="btn-primary w-full justify-center text-sm"
              disabled={codeForm.formState.isSubmitting}
            >
              {codeForm.formState.isSubmitting ? 'Verifying…' : 'Verify and sign in'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
